// 用户事件监听 Hook
// 注意：本文件位于 modules/user/hooks，绝对不能 import 任何 barrel（@/components、
// ../../index 等），否则会立刻产生长链循环依赖，破坏路由级懒加载拆包。

import { useEvent } from "../../../hooks/useEvent";
import { useUserStore } from "../stores";
import { Message } from "../../../components/Base/Message";
import { triggerPopup } from "../../../utils/popupTriggers";
import { POPUP_IDS } from "../../../config/popupConfig";
import i18n from "../../../i18n";
import { firstRechargeStagedApi } from "../../../api/firstRechargeStagedApi";
import { useHomeDisplayStore } from "../../homeDisplay/stores";
import { syncAndMarkPendingStagedClaimAfterDeposit } from "../../firstRechargeStaged/syncPendingClaimAfterDeposit";
import {
  markPendingGroupedClaimConfigResync,
  clearPendingGroupedClaimConfigResync,
} from "../../../components/Event/groupedRechargeClaimReadStorage";
import { ROUTES } from "../../../types/routes";
import {
  isPayLinkConfirmSessionActive,
  markRechargeConfirmedDuringPayLinkSession,
} from "../../../constants/payLinkConfirmSession";

/** 与充值优惠同一套 PROMOTION WS；首充分批入账后 extra.kind 为此值 */
const WS_PROMOTION_KIND_FIRST_RECHARGE_STAGED = "first_recharge_staged";

/**
 * 用户事件监听 Hook
 * 监听来自 WebSocket 的用户相关事件
 */
export function useUserEvents() {
  const { isAuthenticated, loadUserInfo } = useUserStore(state => ({
    isAuthenticated: state.isAuthenticated(),
    loadUserInfo: state.loadUserInfo,
  }));

  useEvent("event", async data => {
    console.log("[useUserEvents] 收到事件数据:", data);
    // 重新加载用户信息
    if (!isAuthenticated) {
      console.log("[useUserEvents] 用户未认证，跳过处理");
      return;
    }

    // 获取事件类型
    const event = data && data.data && data.data.event;

    if (event === "RECHARGE_SUCCESS") {
      const userAlreadyHadFirstRecharge =
        !!useUserStore.getState().userInfo?.firstRechargeFlag;
      // 外链确认会话进行中：记录真到账，「我已付款」点击路径据此区分真付/假点
      markRechargeConfirmedDuringPayLinkSession();
      Message.success(String(i18n.t("deposit.depositSuccess")), 2000);
      // 到账条等依赖 activities.GROUPED_RECHARGE_TASK.popupItems，必须用 refreshConfig 绕开
      // homeDisplayService.loadConfig() 的 2s 节流（否则会直接返回旧 data，不请求接口）
      try {
        await useHomeDisplayStore.getState().refreshConfig();
        // 非首页时下一轮 getConfig 仍会跑；回到首页后再强拉一次（见 useGroupedRechargeClaimPopup）
        markPendingGroupedClaimConfigResync();
        console.log("[useUserEvents] 充值成功，已强制刷新首页配置");

        // 已在首页时 pathname 不变，useGroupedRechargeClaimPopup 的 [pathname] effect 不重跑；
        // 后端可能在 WS 之后才写入 popupItems，需在此直接调度 450ms 延迟补拉。
        const currentPath = window.location.pathname;
        const isAlreadyHome =
          currentPath === ROUTES.HOME ||
          currentPath === ROUTES.HOME_ALT ||
          currentPath === "/";
        if (isAlreadyHome) {
          window.setTimeout(async () => {
            try {
              await useHomeDisplayStore.getState().refreshConfig();
              clearPendingGroupedClaimConfigResync();
            } catch {
              /* ignore */
            }
          }, 450);
        }
      } catch (error) {
        console.error("[useUserEvents] 重新加载配置失败:", error);
      }
      try {
        await syncAndMarkPendingStagedClaimAfterDeposit({
          userAlreadyHadFirstRecharge,
        });
      } catch {
        /* ignore */
      }

      // TODO(webpush-debug): 验证完外链首充弹窗后删除
      console.log("[webpush-debug] WS RECHARGE_SUCCESS", {
        userAlreadyHadFirstRecharge,
        payLinkSessionActive: isPayLinkConfirmSessionActive(),
        willDispatch:
          !userAlreadyHadFirstRecharge && !isPayLinkConfirmSessionActive(),
      });
      // 外链确认弹窗进行中：弹出权交给用户点「我已付款」后的 handleRechargeSuccess，
      // WS 到账先于点击时不得抢先弹
      if (!userAlreadyHadFirstRecharge && !isPayLinkConfirmSessionActive()) {
        window.dispatchEvent(new CustomEvent("webPush-first-deposit-trigger"));
      }
    }
    if (event === "PROMOTION") {
      const extra = (data as { data?: { extra?: Record<string, unknown> } })
        ?.data?.extra;
      if (extra?.kind === WS_PROMOTION_KIND_FIRST_RECHARGE_STAGED) {
        try {
          const snapshot = await firstRechargeStagedApi.fetchSnapshot();
          useHomeDisplayStore.getState().patchFirstRechargeStaged(snapshot);
        } catch (e) {
          console.error(
            "[useUserEvents] first_recharge_staged snapshot refresh failed",
            e
          );
        }
      }
      if (extra?.type === "DAILY_RECHARGE") {
        setTimeout(() => {
          triggerPopup(POPUP_IDS.SEVEN_DAY_RECHARGE);
        }, 500);
      }
    }

    try {
      await loadUserInfo();
    } catch (error) {
      console.error("[useUserEvents] 重新加载用户信息失败:", error);
    }
  });
}
